import './ServicePropertiesEdit.scss';

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { BdaService, BdaServiceProperty } from "../../models/BdaService";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { sectionLabelsSelector } from "../../slices/sectionsSlice";
import { Alert, Badge, Breadcrumb, Button, Card, Container, Form, Modal } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Flex, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import AlidaLoader from "../../components/common/AlidaLoader";
import { Applications } from "../../api/Applications";
import ServicePropertiesForm from "../../components/designer/ServicePropertiesForm";
import ServicePropertyFormItem from "../../components/designer/ServicePropertyFormItem";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { BdaApplication } from "../../models/BdaApplication";
import { Services } from "../../api/Services";

export default function ServicePropertiesEdit() {
  const { id, serviceId } = useParams();
  const navigate = useNavigate();
  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, 'services'));
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, 'applications'));
  const envVarLabels = useSelector((state: RootState) => sectionLabelsSelector(state, 'environment-variables'));

  const [application, setApplication] = useState<BdaApplication>();
  const [service, setService] = useState<BdaService>();
  const [error, setError] = useState(false);
  const [property, setProperty] = useState<BdaServiceProperty>();
  const [showPropertyModal, setShowPropertyModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  useEffect(() => {
    if (id) initApplication();
    if (serviceId) initService();
    // eslint-disable-next-line
  }, []);

  const initService = () => {
    Services.getService(+serviceId!)
      .then(
        ({ data }) => setService(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-error', message, `${serviceLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  };

  const initApplication = () => {
    Applications.getApplication(+id!)
      .then(
        ({ data }) => setApplication(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('application-error', message, `${applicationLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  };

  const handleSubmit = useCallback(() => {
    showLoader();
    const body = {
      services: service!.properties.map(prop => ({
        serviceLabel: service!.name,
        propertyKey: prop.key,
        value: prop.value,
      }))
    };
    Applications.editServiceProperties(+id!, body)
      .then(
        ({ data }) => {
          hideLoader();
          showToast('service-properties-edit-success', `${serviceLabels.singular} properties updated successfully.`, 'Success', 'success');
          navigate(`/bda-applications/${data.id}`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-error', message, `${serviceLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [service, id, showToast, navigate, serviceLabels, showLoader, hideLoader]);

  const openPropertyModal = useCallback((prop: BdaServiceProperty) => () => {
    setProperty(prop);
    setShowPropertyModal(true);
  }, []);

  const handlePropertyModalClose = useCallback(() => {
    setShowPropertyModal(false);
    setProperty(undefined);
  }, []);

  const handleUpdateServiceProperties = useCallback((properties: BdaServiceProperty[]) => {
    setService(curr => ({ ...curr!, properties: properties || curr!.properties }));
  }, []);

  const toggleInterpolation = useCallback(() => {
    setProperty(curr => ({ ...curr!, interpolationEnabled: !curr?.interpolationEnabled }));
  }, []);

  const handlePropChange = useCallback((_key: string, checkbox?: boolean, pure?: boolean) => (evt: any) => {
    const value = pure ? evt : checkbox ? evt.target.checked : evt.target.value;
    setProperty(curr => ({ ...curr!, value }));
  }, []);

  const submitProperty = useCallback(() => {
    setService(curr => {
      const updatedProperties = curr!.properties!.map(p => p.key === property!.key ? property! : p);
      return { ...curr!, properties: updatedProperties }
    })
    handlePropertyModalClose();
  }, [property, handlePropertyModalClose]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-circle')} />
        <span className="ms-2">An error occurred, try again later.</span>
      </Alert>
    );

    return service
      ? (
        <Card className="mb-3">
          <Card.Header className="bg-white p-2">
            <Card.Title className="m-1">Edit {serviceLabels.singular} Properties</Card.Title>
          </Card.Header>

          <Card.Body className="px-0 pb-0">
            {service!.properties && (
              <ServicePropertiesForm
                properties={service!.properties}
                onPropertyModalOpen={openPropertyModal}
                isFromPage
                handleUpdateProperties={handleUpdateServiceProperties}
              />
            )}
          </Card.Body>

          <Card.Footer className="d-flex justify-content-end">
            <Button variant="success" size="sm" onClick={handleSubmit}>
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Save Changes</span>
            </Button>
          </Card.Footer>
        </Card>
      )
      : (
        <div className="p-5 text-center">
          <AlidaLoader size={350} />
        </div>
      )
  }, [error, service, serviceLabels, openPropertyModal, handleUpdateServiceProperties, handleSubmit]);

  return (
    <Container className="service-properties-edit mt-3">
      <Helmet>
        <title>Edit {serviceLabels.singular} {service?.name || ""} Properties</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/bda-applications" }}>{applicationLabels.plural}</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/bda-applications/${id}` }}>{application?.name || applicationLabels.singular}</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/services/${serviceId}` }}>{service?.name || serviceLabels.singular}</Breadcrumb.Item>
        <Breadcrumb.Item active>Edit Properties</Breadcrumb.Item>
      </Breadcrumb>

      {renderAsyncContent()}

      <Modal show={showPropertyModal} onHide={handlePropertyModalClose} size={property?.valueType === "JSON" ? "xl" : "lg"}>
        <Modal.Header closeButton>
          <Modal.Title>Edit {property?.key} property</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Flex justify="flex-end" className="mb-2">
            <Tooltip
              title={(
                <>
                  <span>{envVarLabels.plural} can be interpolated with <Badge bg="dark" className="p-1">{"{{ variableName }}"}</Badge></span>
                </>
              )}
              mouseEnterDelay={0.5}
            >
              <Button size="sm" variant="light" className="border" onClick={toggleInterpolation}>
                <FontAwesomeIcon icon={solid('code')} />
                <span className="ms-2">Toggle Interpolation</span>
              </Button>
            </Tooltip>
          </Flex>
          <Form.Group controlId={property?.valueType !== 'BOOLEAN' ? `modal-${property?.key}-form-control` : undefined}>
            {property && <ServicePropertyFormItem prop={property} onPropChange={handlePropChange} wideMode />}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button size="sm" variant="light" className="border" onClick={handlePropertyModalClose}>Cancel</Button>
          <Button size="sm" variant="success" onClick={submitProperty}>Confirm</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}